import { useAddCoupon, useCart, useEmptyCart, useFillCart } from "@hooks/queries"
import { displayErrorAlert } from "@hooks/useAlertOverlay"
import { useIsSSR } from "eddev/routing"
import { useEffect, useMemo, useRef, useState } from "react"
import { CartContentFragment, CartErrorsFragment, CartErrorType } from "../../../types.graphql"

type SyncedCartItem = { productId: number; variationId?: number; quantity: number }

type SyncedCartSpec = {
  doNotSync?: boolean
  orderId?: number
  items: SyncedCartItem[]
  coupons: string[]
  enabled?: boolean
}

export function useSyncCart(spec: SyncedCartSpec) {
  const [cartData, setCartData] = useState<{
    content: CartContentFragment | null
    errors: (CartErrorsFragment | null)[]
  }>({
    errors: [],
    content: null,
  })

  const hash = JSON.stringify(spec)

  const ssr = useIsSSR()
  const enabled = spec.enabled !== false && !ssr
  const cart = useCart(undefined, {
    enabled: enabled,
  })

  useEffect(() => {
    if (cart.data) {
      setCartData({
        content: cart.data.cart ?? null,
        errors: [],
      })
    }
  }, [cart.data])

  const addCoupon = useAddCoupon({
    onSuccess: (data) => {
      setCartData({
        content: data.applyCoupon?.cart ?? null,
        errors: [],
      })
    },
  })

  const empty = useEmptyCart()
  const fill = useFillCart({
    onSuccess: (data) => {
      setCartData({
        content: data.fillCart?.cart ?? null,
        errors: data.fillCart?.cartErrors ?? [],
      })
    },
    onError: (error) => {
      setCartData({
        content: null,
        errors: [
          {
            reasons: error.messages,
            type: CartErrorType.Unknown,
          },
        ],
      })
      cart.refetch()
    },
  })

  const isLoading = cart.isLoading || fill.isPending || empty.isPending || addCoupon.isPending

  function isCartSynced(data?: CartContentFragment | null) {
    if (!data?.contents?.nodes.length && !spec.items.length) {
      // Nothing in cart, nothing enforced
      return true
    }
    let matches = true
    // console.log("Spec is", spec)
    // Ensure the contents match exactly
    if ((data?.contents?.nodes.length ?? 0) !== (spec.items.length ?? 0)) {
      matches = false
      // console.log("Different length", data?.contents?.nodes.length, spec.items.length)
    } else {
      data?.contents?.nodes.forEach((node) => {
        const item = spec.items.find((i) => i.productId == node.product?.node.databaseId || i.productId == node.variation?.node.databaseId)
        if (!item || item.quantity !== node.quantity || (item.variationId && item.variationId !== node.variation?.node.databaseId)) {
          matches = false
          // console.log("Item doesnt match", item, node)
        }
      })
    }
    // if ((spec.coupons.length ?? 0) != (data?.appliedCoupons?.length ?? 0)) {
    //   matches = false
    //   console.log("Coupons dont match", spec.coupons, data?.appliedCoupons)
    // }
    return matches
  }

  const syncAttempts = useRef(0)

  useEffect(() => {
    syncAttempts.current = 0
  }, [hash])

  const fillCart = async (args: { items: { productId: number; variationId?: number; quantity: number }[]; coupons: string[] }) => {
    if (cartData.content?.contents?.nodes.length! > 0) {
      await empty.mutateAsync({
        input: {},
      })
      setCartData({
        content: null,
        errors: [],
      })
    }
    if (args.items.length === 0) return
    try {
      const result = await fill.mutateAsync({
        input: {
          // coupons: args.coupons,
          items: args.items,
        },
      })
      if (!isCartSynced(result.fillCart?.cart!)) {
        // console.log("Not synced?")
      }
      if (isCartSynced(result.fillCart?.cart!)) {
        setCartData({
          content: result.fillCart?.cart!,
          errors: [],
        })
        syncAttempts.current = 0
      }
    } catch (err) {
      syncAttempts.current++
    }
  }

  useEffect(() => {
    // console.log("Syncing", [spec.doNotSync, spec.items.length === 0, !cart.data, !enabled])
    if (spec.doNotSync || spec.items.length === 0 || !cart.data || !enabled) return
    if (syncAttempts.current > 3) {
      displayErrorAlert("We're having trouble syncing your cart. Please try again later.", {
        confirmLabel: "Reload Page",
        onConfirm: () => {
          document.location.reload()
        },
      })
      return
    }

    if (isLoading) return
    if (isCartSynced(cartData.content)) return
    fillCart({ items: spec.items, coupons: spec.coupons })
    // console.log("Filling cart")
  }, [hash, isLoading, cartData.content, !cart.data, !enabled])

  const isSynced = useMemo(() => isCartSynced(cartData.content), [cartData.content, hash])

  return {
    success: isSynced,
    data: cartData.content,
    errors: cartData.errors,
    pending: isLoading,
    refresh: cart.refetch,
    async addCoupon(couponCode: string) {
      return addCoupon.mutateAsync({
        couponCode,
      })
    },
  }
}
